import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { contactTypeReducer } from "../../../state/reducers";
import { IContactType } from "../../../utils/types/models";

const ContactTypeEditor: React.FC<IAddEntityScreenProps<IContactType> | IEditEntityScreenProps<IContactType>> = (props: IAddEntityScreenProps<IContactType> | IEditEntityScreenProps<IContactType>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(contactTypeReducer, getInitialState<IContactType>());
	const initialContactType: IContactType = {
		id: (props as IEditEntityScreenProps<IContactType>).recordId || 0,
		name: "",
		active: true
	};
	const [contactType, setContactType] = useState<IContactType>(initialContactType);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setContactType(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setContactType(handleChange(contactType, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(contactType.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: IContactType): void {
		if (record && !close) {
			setContactType(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<IContactType>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={contactType}
			endpoint={Endpoint.ContactTypes}
			entityState={state}
			entityType={t("contactType")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={contactType.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={contactType.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={contactType.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default ContactTypeEditor;
